import _ from 'underscore';
import React, { useContext, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withUnvalidatedControlErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { Alert } from '@bingads-webui-react/alert';
import { Text, Label, Checkbox } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { Select } from '@bingads-webui-react/primitive';
import { getRouterDep } from '@bingads-webui-campaign-react/legacy-router-dep';
import { withScope } from '@bingads-webui-react/scope-service';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { FeedFilterPicker } from '@bingads-webui-campaign-react/feed-filter';
import { getStoreStyles } from '../styles/get-store-style';

const validate = (i18n, campaignObjective, data) => {
  const validationErrors = campaignObjective === 'ShoppingCampaign' && !data.ProviderId ?
    [i18n.getString(_TL_('At least one store needs to be selected.'))] : [];

  if (campaignObjective === 'ShoppingCampaign' && data.IsFeedFiltered && !data.FeedLabel && !data.SalesCountry) {
    validationErrors.push(i18n.getString(_TL_('Either feed label or country/region of sale needs to be selected with the filter enabled. If you want to disable the filter, please click "Use all products" button.')));
  }

  return validationErrors;
};

function StoreSelectCreation(props) {
  const {
    hasStores,
    stores,
    countries,
    campaignObjective,
    updateValidation,
    scope,
    siteMap,
    permissions,
    validationProps,
    showProductCollections,
    hideFeedLabelAndCountry,
  } = props;

  const { data } = validationProps;
  const {
    dynamic: {
      FeedLabel: feedLabelEnabled,
      FeedLabelBlockList: feedLabelBlockListEnabled,
    },
  } = permissions;
  const isFeedLabelEnabled = feedLabelEnabled && !feedLabelBlockListEnabled;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStoreStyles);

  const onDataChange = useCallback((dataPatch) => {
    const newData = {
      ...data,
      ...dataPatch,
    };
    const validationErrors = validate(i18n, campaignObjective, newData);

    updateValidation(validationErrors, newData);
  }, [i18n, campaignObjective, updateValidation, data]);

  const onSelectedStoreChange = useCallback((event) => {
    const storeId = event.target.value;

    onDataChange({
      ProviderId: storeId,
    });
  }, [onDataChange]);

  useEffect(() => {
    let newData = data;
    if (campaignObjective === 'ShoppingCampaign' && _.keys(stores).length === 1) {
      const preSelectedStoreId = Object.keys(stores)[0];
      newData = {
        ...data,
        ProviderId: preSelectedStoreId,
      };
    }
    if (isFeedLabelEnabled) {
      newData = {
        ...newData,
        FeedLabel: null,
        SalesCountry: null,
      };
    } else {
      newData = {
        ...newData,
        FeedLabel: null,
        SalesCountry: 'US',
      };
    }
    const validationErrors = validate(i18n, campaignObjective, newData);
    updateValidation(validationErrors, newData, false);

    // Ignoring deps to fire only once this store pre-select logic
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectedCountryChange = useCallback((event) => {
    const countryId = event.target.value;
    onDataChange({
      SalesCountry: countryId,
    });
  }, [onDataChange]);

  const onIsFeedFilteredChange = useCallback((isFeedFiltered) => {
    onDataChange({
      IsFeedFiltered: isFeedFiltered,
      SalesCountry: isFeedFiltered ? data.SalesCountry : null,
      FeedLabel: isFeedFiltered ? data.FeedLabel : null,
    });
  }, [data.FeedLabel, data.SalesCountry, onDataChange]);

  const onSelectedFeedLabelChangeByValue = useCallback((value) => {
    onDataChange({
      FeedLabel: value,
    });
  }, [onDataChange]);

  const onSelectedSaleCountryChangeByValue = useCallback((value) => {
    onDataChange({
      SalesCountry: value,
    });
  }, [onDataChange]);

  const goToBMC = () => {
    const router = getRouterDep(scope, siteMap);
    router.navigateToRoute('bing_merchant_center');
  };

  const onLocalChecked = useCallback((event) => {
    const isChecked = event.target.checked;
    onDataChange({
      LocalInventoryAdsEnabled: isChecked,
    });
  }, [onDataChange]);

  const onProductCollectionChecked = useCallback((event) => {
    const isChecked = event.target.checked;
    onDataChange({
      ProductCollectionsEnabled: isChecked,
    });
  }, [onDataChange]);

  const storeFieldId = useId('storeInput');
  const countryFieldId = useId('countryInput');
  const localInventoryFieldId = useId('localInventoryInput');
  const productCollectionFieldId = useId('productCollectionInput');

  return (
    <div className={classNames('store-settings-creation', classes.storeSettings)}>
      <div className={classNames('strore-theme-info-pre-selectedStoreId-items', classes.preselectedStoreIdItemsStyle)}>
        {/* user doesn’t have store */}
        {!hasStores ?
          <div className={classNames('select-store-no-store', classes.warning_panel_bottom)}>
            <Alert
              message={i18n.getString(_TL_('No merchant center stores found. Create a store in Merchant Center in order to run product ads.'))}
              level="Info"
              icon={(<div className="alert-image" />)}
              dismissible={false}
              action={{
                callback: goToBMC,
                message: i18n.getString(_TL_('Create a store')),
              }}
            />
          </div>
          :
          <div >
            {/* user has store + selects "sell products from catalog" + not selects "sell products from catalog" */}
            <div className={classNames(classes.select_label)}>
              <Text>{i18n.getString(_TL_('Select a Merchant Center store to advertise your products.'))}</Text>
            </div>
            <Label htmlFor={storeFieldId}>
              {i18n.getString(_TL_('Store'))}
              <HelpPopup aria-label="Store" topic="pop_BA_PMAX_Store" />
            </Label>
            <Select
              id={storeFieldId}
              className={classNames('store-select', classes.select, classes.control)}
              value={data.ProviderId}
              onChange={onSelectedStoreChange}
            >
              <option key="select-a-store" value="">{i18n.getString(_TL_('Select account'))}</option>
              {_.pairs(stores).map(([key, { text }]) =>
              (
                <option key={key} value={key}>{text}</option>
              ))}
            </Select>

            {(campaignObjective === 'ShoppingCampaign' || data.ProviderId) &&
              (!hideFeedLabelAndCountry)
              &&
              <>
                {isFeedLabelEnabled ? (
                  <FeedFilterPicker
                    className={classNames('feed-select', classes.control)}
                    isFeedFiltered={data.IsFeedFiltered}
                    onIsFeedFilteredChange={onIsFeedFilteredChange}
                    saleCountries={_.pairs(countries).map(([key, { text }]) => ({ key, text }))}
                    selectedFeedLabel={data.FeedLabel}
                    onSelectedFeedLabelChange={onSelectedFeedLabelChangeByValue}
                    selectedSaleCountry={data.SalesCountry}
                    onSelectedSaleCountryChange={onSelectedSaleCountryChangeByValue}
                    storeId={data.ProviderId}
                  />
                ) : (
                  <>
                    <Label htmlFor={countryFieldId}>
                      {i18n.getString(_TL_('Country/region of sale'))}
                    </Label>
                    <Select
                      id={countryFieldId}
                      className={classNames('country-select', classes.select, classes.control)}
                      value={data.SalesCountry}
                      onChange={onSelectedCountryChange}
                    >
                      {_.pairs(countries).map(([key, { text }]) =>
                      (
                        <option key={key} value={key}>{text}</option>
                      ))}
                    </Select>
                  </>
                )}
                <Label htmlFor={localInventoryFieldId}>
                  {i18n.getString(_TL_('Local inventory ads'))}
                  <HelpPopup aria-label="Local" topic="pop_BA_PMAX_Local" />
                </Label>
                <Checkbox
                  id={localInventoryFieldId}
                  className={classNames('local-inventory', classes.control)}
                  label={i18n.getString(_TL_('Enable local inventory ads'))}
                  onChange={onLocalChecked}
                  checked={data.LocalInventoryAdsEnabled}
                />
                {/* TODO: should also add this field to edit page when setting page finalized. for now. add this to creation page first. */}
                {showProductCollections &&
                  <>
                    <Label htmlFor={productCollectionFieldId}>
                      {i18n.getString(_TL_('Product collections'))}
                      {/* <HelpPopup aria-label="Local" topic="pop_BA_PMAX_Local" /> */}
                    </Label>
                    <Checkbox
                      id={productCollectionFieldId}
                      className={classNames('product-collections', classes.control)}
                      label={i18n.getString(_TL_('Enable collections'))}
                      onChange={onProductCollectionChecked}
                      checked={data.ProductCollectionsEnabled}
                    />
                  </>
                }

                {/* Scenario: customers link a store and we want to tell them the link can’t be changed once they go to the next step */}
                <div className={classNames('select-store-store-selected-warning', classes.warning_panel_bottom)}>
                  <Alert
                    message={
                      isFeedLabelEnabled ? (
                        i18n.getString(_TL_('Note: While you can always change the feed label or country/region of sale you use, doing so will prompt new performance data. You can switch back to the original feed label or country/region of sale to review its performance data; however, the performance data for each cannot merge. '))
                      ) : (
                        i18n.getString(_TL_('Once you select a Store and Country/region of sale for this campaign, you cannot change them. If at some point you want to add a different store, you’ll need to create a new campaign. '))
                      )
                    }
                    level="Info"
                    icon={(<div className="alert-image" />)}
                    dismissible={false}
                  />
                </div>
              </>
            }

            {(campaignObjective !== 'ShoppingCampaign' && !data.ProviderId) && !hideFeedLabelAndCountry &&
              <div className={classNames('select-store-no-store', classes.warning_panel_bottom)}>
                <Alert
                  message={i18n.getString(_TL_('Without a store you won’t be able to use product ads and can’t add it later.'))}
                  level="Info"
                  icon={(<div className="alert-image" />)}
                  dismissible={false}
                />
              </div>
            }
          </div>
        }
      </div>
    </div>
  );
}

StoreSelectCreation.propTypes = {
  hasStores: PropTypes.bool.isRequired,
  stores: PropTypes.shape({
    StoreId: PropTypes.string,
    Name: PropTypes.string,
  }).isRequired,
  countries: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  campaignObjective: PropTypes.string.isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  scope: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      FeedLabel: PropTypes.bool,
      FeedLabelBlockList: PropTypes.bool,
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      IsFeedFiltered: PropTypes.bool,
      FeedLabel: PropTypes.string,
      SalesCountry: PropTypes.string,
      LocalInventoryAdsEnabled: PropTypes.bool,
      ProviderId: PropTypes.string,
      ProductCollectionsEnabled: PropTypes.bool,
    }).isRequired,
    alwaysShowError: PropTypes.bool,
  }).isRequired,
  showProductCollections: PropTypes.bool,
  hideFeedLabelAndCountry: PropTypes.bool,
};

StoreSelectCreation.defaultProps = {
  showProductCollections: false,
  hideFeedLabelAndCountry: false,
};

export const ValidatedStoreCreation = withError(withControlErrorHandling(withUnvalidatedControlErrors(withCustomValidation(withScope(withSiteMap(withPermissions(StoreSelectCreation)))))));
